import React, { useState, useEffect } from 'react';
import { ref, onValue, remove, update, get, runTransaction, push } from "firebase/database";
import { db } from "../firebase";
import './ActiveTrades.css';

const ActiveTrades = ({ selectedSymbol }) => {
  const [positions, setPositions] = useState([]);
  const [liveMarketPrices, setLiveMarketPrices] = useState({});
  const [showClosedNotify, setShowClosedNotify] = useState(false);
  const [lastClosedData, setLastClosedData] = useState(null);
  const savedUser = JSON.parse(localStorage.getItem('user'));

  // ✅ Fetch active positions
  useEffect(() => {
    if (!savedUser?.username) return;
    const activeRef = ref(db, `activePositions/${savedUser.username}`);
    return onValue(activeRef, (snapshot) => {
      const data = snapshot.val();
      setPositions(data ? Object.keys(data).map(key => ({ dbKey: key, ...data[key] })) : []);
    });
  }, [savedUser?.username]);

  // ✅ Live market prices
  useEffect(() => {
    if (positions.length === 0) return;

    const uniqueAssets = [...new Set(positions.map(p => p.asset.replace('/USDT', '').toLowerCase()))];
    const streams = uniqueAssets.map(asset => `${asset}usdt@ticker`).join('/');
    const ws = new WebSocket(`wss://stream.binance.com:9443/ws/${streams}`);

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      const symbol = data.s.replace('USDT', '');
      setLiveMarketPrices(prev => ({
        ...prev,
        [symbol]: parseFloat(data.c).toFixed(2)
      }));
    };

    return () => ws.close();
  }, [positions]);

  // ✅ MAIN LOGIC (smooth pnl)
  useEffect(() => {
    const timer = setInterval(async () => {
      if (positions.length === 0) return;

      const updates = {};
      const now = Date.now();

      positions.forEach(pos => {
        if (pos.status !== 'Active') return;

        const remaining = Math.max(0, Math.floor((pos.expiryTime - now) / 1000));
        const progress = (pos.duration - remaining) / pos.duration;
        const target = Number(pos.targetPnL) || 0;

        if (remaining > 0) {
          let livePnL = 0;
          const smoothProgress = progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

          const startOffset = target > 0
            ? -(pos.amount * 0.05)
            : (pos.amount * 0.05);

          livePnL = startOffset + (target - startOffset) * smoothProgress;

          updates[`activePositions/${savedUser.username}/${pos.dbKey}/currentLivePnL`] =
            parseFloat(livePnL.toFixed(2));
        }

        if (remaining <= 0 && !pos.finalized) {
          updates[`activePositions/${savedUser.username}/${pos.dbKey}/finalized`] = true;
          handleFinalize(pos, target);
        }
      });

      if (Object.keys(updates).length > 0) {
        await update(ref(db), updates);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [positions, savedUser?.username]);

  // ✅ FINALIZE & MOVE TO HISTORY
  const handleFinalize = async (pos, finalPnL) => {
    try {
      const userRef = ref(db, `users/${savedUser.username}`);

      // 1. Update User Balance
      await runTransaction(userRef, (userData) => {
        if (userData) {
          userData.balance = parseFloat(
            (Number(userData.balance || 0) + finalPnL).toFixed(2)
          );
        }
        return userData;
      });

      // 2. Push to Transactions History (For User History Page)
      const historyRef = ref(db, 'transactions');
      await push(historyRef, {
        username: savedUser.username,
        type: 'Trade',
        pair: pos.asset.toUpperCase(),
        amount: finalPnL,
        direction: pos.direction,
        investment: pos.amount,
        status: 'Success', // History mein "Completed" show hoga
        timestamp: Date.now()
      });

      // 3. Clean up queues
      if (pos.queueTradeId) {
        await remove(ref(db, `tradingControl/${savedUser.username}/queues/${pos.queueTradeId}`));
      }

      // 4. Remove from Active
      await remove(ref(db, `activePositions/${savedUser.username}/${pos.dbKey}`));

      setLastClosedData({
        asset: pos.asset.replace('/USDT', ''),
        pnl: finalPnL
      });

      setShowClosedNotify(true);
      setTimeout(() => setShowClosedNotify(false), 3000);

    } catch (e) {
      console.error("Settlement Error:", e);
    }
  };

  return (
    <div className="positions-container professional-theme">

      {showClosedNotify && lastClosedData && (
        <div className="premium-close-popup">
          <div className="popup-content">
            <span className="p-asset">{lastClosedData.asset.toUpperCase()}/USDT</span>
            <span className="p-label">Settled Result</span>
            <span className={`p-result ${lastClosedData.pnl >= 0 ? 'text-up' : 'text-down'}`}>
              {lastClosedData.pnl >= 0 ? 'WIN' : 'LOSS'} / {lastClosedData.pnl >= 0 ? '+' : ''}{lastClosedData.pnl} USDT
            </span>
          </div>
        </div>
      )}

      <h3 className="section-title">Active Positions ({positions.length})</h3>

      {positions.length === 0 ? (
        <div className="no-positions">No active trades running</div>
      ) : (
        positions.map(pos => {
          const displayAsset = pos.asset.replace('/USDT', '').toUpperCase();
          const isProfit = parseFloat(pos.currentLivePnL) >= 0;
          const feeRate = 0.0325; 
          const fixedFee = (pos.amount * feeRate).toFixed(2);
          const remainingSeconds = Math.max(0, Math.floor((pos.expiryTime - Date.now()) / 1000));
          const progressWidth = (remainingSeconds / pos.duration) * 100;

          return (
            <div key={pos.dbKey} className="trade-card-pro">
              <div className="card-header">
                <div className="pair-info">
                  <span className={`direction-badge ${pos.direction.toLowerCase()}`}>
                    {pos.direction}
                  </span>
                  <span className="asset-name">{displayAsset}/USDT 20x</span>
                </div>
              </div>

              <div className="stats-grid">
                <div className="stat">
                  <span className="label">Investment</span>
                  <span className="value">${pos.amount}</span>
                </div>

                <div className="stat">
                  <span className="label">Trading Fee</span>
                  <span className="value gold">-${fixedFee}</span>
                </div>

                <div className="stat">
                  <span className="label">Live Profit</span>
                  <span className={`value bold-pnl ${isProfit ? 'text-up' : 'text-down'}`}>
                    {isProfit ? '+' : ''}{pos.currentLivePnL} USDT
                  </span>
                </div>

                <div className="stat">
                  <span className="label">PnL (%)</span>
                  <span className={`value ${isProfit ? 'text-up' : 'text-down'}`}>
                    {((parseFloat(pos.currentLivePnL) / pos.amount) * 100).toFixed(2)}%
                  </span>
                </div>
              </div>

              <div className="timer-section">
                <div className="timer-text">
                  Settling in: <span>{remainingSeconds}s</span>
                </div>
                <div className="progress-track">
                  <div
                    className="progress-bar-fill"
                    style={{
                      width: `${progressWidth}%`,
                      transition: 'width 1s linear'
                    }}
                  ></div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default ActiveTrades;